import React from 'react';
import { SortableList } from '../../../../components/SortableList/SortableList';
import { Icon } from '../../../../components/Icon/Icon';
import { t } from '../../../../locales/translate';
import { Order, OutgoingItemGroup, IncomingItemGroup } from '../../../../model/order';
import { compact } from 'lodash';
import { Selection, formatDateTime } from '../../../../util/util';
import { PersonalIdentifier } from '../../../../components/PersonalIdentifier/PersonalIdentifier';
import { Seat } from '../../../../model/ticket';
import { SeatInfo } from '../../../../components/SeatInfo/SeatInfo';
import { orderPath } from '../../../../routes';
import { ExternalLink } from '../../../../components/ExternalLink/ExternalLink';

interface Props {
  group: OutgoingItemGroup | IncomingItemGroup;
  order: Order;
  isPollingTicketUrls: boolean;
  selection: Selection<string>;
}

interface SingleItem {
  id: string;
  index: number;
  validationCode?: string | null;
  ticketPassword?: string | null;
  personalIdentifier?: string | null;
  usedAt?: string;
  url?: string | null;
  seat?: Seat | null;
  passbookUrl?: string;
  resaleOffer?: {
    code: string;
    status: string;
    public: boolean;
    privateUrl: string | null;
    newOrderUrl: string | null;
    newOrderReference: string | null;
  };
  resoldByOffer?: {
    originalOrderUrl: string;
    originalOrderReference: string;
    soldItemValidationCode: string;
  };
}

export function ItemsSubList({ group, order, isPollingTicketUrls, selection }: Props): JSX.Element {
  let items: SingleItem[] = group.itemIds.map((id, index) => ({ id, index }));
  let isEvent = false;
  if (group.__typename === 'OutgoingItemGroup' && group.tickets && group.tickets.length > 0) {
    items = group.tickets.map((e, index) => ({
      id: e.id,
      validationCode: e.validationCode,
      ticketPassword: e.ticketPassword,
      usedAt: compact([formatDateTime(e.usedAt), e.validatedBy]).join(', '),
      personalIdentifier: e.personalIdentifier,
      url: e.url,
      seat: e.seat,
      index,
      passbookUrl: e.passbook?.url,
      resaleOffer: e.resaleOffer
        ? {
            code: e.resaleOffer.code,
            status: e.resaleOffer.status,
            public: e.resaleOffer.public,
            privateUrl: e.resaleOffer.privateUrl,
            newOrderUrl: e.resaleOffer.newOrderId && orderPath(e.resaleOffer.newOrderId),
            newOrderReference: e.resaleOffer.newOrderReference,
          }
        : undefined,
      resoldByOffer: e.resoldByOffer
        ? {
            originalOrderUrl: orderPath(e.resoldByOffer.originalOrderId),
            originalOrderReference: e.resoldByOffer.originalOrderReference,
            soldItemValidationCode: e.resoldByOffer.soldItemValidationCode,
          }
        : undefined,
    }));
    isEvent = true;
  }
  const selectedItems = items.filter((e) => selection.has(e.id));

  return (
    <SortableList<SingleItem>
      items={items}
      onRowSelect={(e) => selection.toggle(e.id)}
      multiSelect
      size="sm"
      selectedItems={selectedItems}
      columns={
        isEvent
          ? [
              {
                dataKey: 'validationCode',
                label: t.model.item.validationCode,
                cellRenderer: (row) => (
                  <>
                    <div>
                      <span title={row.id}>{row.validationCode}</span>{' '}
                      {(row.url || isPollingTicketUrls) && (
                        <a href={row.url ?? ''} target="_blank" rel="noreferrer">
                          <Icon name="download--sm" loading={isPollingTicketUrls} tooltip={t.model.ticket._name} />
                        </a>
                      )}
                      {row.passbookUrl && (
                        <a href={row.passbookUrl} target="_blank" rel="noreferrer">
                          <Icon name="ticket" tooltip={t.model.ticket.passbook} />
                        </a>
                      )}
                    </div>
                    {row.resaleOffer && isResaleOfferActive(row.resaleOffer.status) && (
                      <div>
                        <span>{t.model.ticket.resaleOffer.onResale}</span>
                        {row.resaleOffer.public ? (
                          ` (${t.model.ticket.resaleOffer.public})`
                        ) : (
                          <span>
                            {` (${t.model.ticket.resaleOffer.private})`}
                            <ExternalLink url={row.resaleOffer.privateUrl ?? ''} />
                          </span>
                        )}
                      </div>
                    )}
                    {row.resaleOffer && isResaleOfferPurchased(row.resaleOffer.status) && (
                      <div>
                        <a href={row.resaleOffer.newOrderUrl ?? ''} className="button button--link">
                          {t.model.ticket.resaleOffer.repurchasedInOrder} {row.resaleOffer.newOrderReference}
                        </a>
                      </div>
                    )}
                  </>
                ),
              },
              items.some((e) => e.ticketPassword) && {
                dataKey: 'ticketPassword',
                label: t.model.ticket.ticketPassword,
              },
              items.some((e) => e.resoldByOffer) && {
                dataKey: 'resoldByOffer',
                label: t.model.ticket.resoldByOffer.originalOrder,
                cellRenderer: ({ resoldByOffer }) => (
                  <a href={resoldByOffer?.originalOrderUrl} className="button button--link">
                    {resoldByOffer?.originalOrderReference} ({resoldByOffer?.soldItemValidationCode})
                  </a>
                ),
              },
              {
                dataKey: 'personalIdentifier',
                label: t.model.ticket.personalIdentifier,
                cellRenderer: (row) => (
                  <PersonalIdentifier value={row.personalIdentifier} edit={{ orderId: order.id, itemId: row.id }} />
                ),
              },
              items.some((e) => e.seat) && {
                dataKey: 'seat',
                label: t.model.ticket.seat,
                cellRenderer: (row) => row.seat && <SeatInfo seat={row.seat} />,
              },
              items.some((e) => e.usedAt) && {
                dataKey: 'usedAt',
                label: t.model.item.usedAt,
              },
            ]
          : [{ dataKey: 'index', cellRenderer: (row) => <span title={row.id}>{row.index + 1}</span> }]
      }
    />
  );
}

const RESALE_OFFER_STATUS = {
  ON_SALE: 'on_sale',
  CART: 'cart',
  PURCHASED: 'purchased',
} as const;

const isResaleOfferActive = (status: string): boolean =>
  status === RESALE_OFFER_STATUS.ON_SALE || status === RESALE_OFFER_STATUS.CART;

const isResaleOfferPurchased = (status: string): boolean => status === RESALE_OFFER_STATUS.PURCHASED;
